import { memo, useState, useEffect, useCallback } from "react";
import ActionCard from "src/components/ActionCard";
import {
  CardRadius,
  chartOptions,
  HeightCard,
  generateUniqueColors,
  formatter,
  MESSAGES,
  ROUTES,
} from "src/utils/constants";
import { IoIosArrowForward } from "react-icons/io";
import { Doughnut } from "react-chartjs-2";
import {
  Box,
  Text,
  Group,
  Title,
  Loader,
  useMantineTheme,
  MantineTheme,
} from "@mantine/core";
import Select from "src/components/Select";
import {
  GetPosDashboardQuery,
  SortType,
  useGetPosDashboardLazyQuery,
} from "src/generated/graphql";
import { useNavigate } from "react-router-dom";
import { IChartFilter } from "src/api/context/DashboardContext";

interface IProps {
  filters: IChartFilter;
}

type DataType = {
  labels: string[];
  datasets: {
    data: (
      | {
          value: number;
          __typename?: "DashboardObject" | undefined;
          totalSales: number;
          totalRevenue: number;
          name: string;
        }
      | {
          value: number;
          __typename?: "RevenueItem" | undefined;
          revenue: number;
          category: string;
        }
    )[];
    backgroundColor: string[];
  }[];
};

const optionsGraph = (theme: MantineTheme) => ({
  ...chartOptions,
  plugins: {
    ...chartOptions.plugins,
    tooltip: {
      callbacks: {
        title: (context: any) => {
          const { name, totalSales, category } = context[0].raw;
          return `Name: ${name || category} ${
            totalSales ? "\nSales: " + totalSales : ""
          }`;
        },
        label: (context: any) => {
          const { totalRevenue, revenue } = context.raw;
          return ` Revenue: ${formatter.format(revenue || totalRevenue)}`;
        },
      },
    },
    legend: {
      ...chartOptions.plugins.legend,
      labels: {
        ...chartOptions.plugins.legend.labels,
        color: theme.colors.secondaryTextColor[6],
      },
    },
  },
});

function PointOfSale({ filters }: IProps) {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const [sortType, setSortType] = useState(SortType.RevenueGroup);
  const [getPosDashboard, { data: POSObject, loading }] =
    useGetPosDashboardLazyQuery({
      fetchPolicy: "network-only",
    });

  const [data, setData] = useState<DataType | undefined>(undefined);

  useEffect(() => {
    const { dateRange, locations } = filters;
    if (!dateRange) return;
    getPosDashboard({
      variables: {
        sortType,
        data: {
          startAt: dateRange[0],
          endAt: dateRange[1],
          locations: locations.map((e) => ({ id: e })),
        },
      },
    });
  }, [filters, sortType]);

  useEffect(() => {
    if (!loading) {
      getData(POSObject, sortType);
    }
  }, [POSObject, sortType, loading]);

  const getData = (input: GetPosDashboardQuery | undefined, type: SortType) => {
    let { posDashboard } = input || {};
    let isRevenue = type !== SortType.Product;
    let arrayChart = isRevenue
      ? posDashboard?.revenueItem || []
      : posDashboard?.productObject || [];

    let data = {
      labels: arrayChart.map((e) => ("category" in e ? e.category : e.name)),
      datasets: [
        {
          data: arrayChart.map((e) => ({
            ...e,
            value: "revenue" in e ? e.revenue : e.totalRevenue,
          })),
          backgroundColor: generateUniqueColors(arrayChart.length, 340, 100),
        },
      ],
    };

    setData(data);
  };

  return (
    <ActionCard
      headerOnly
      title="Point Of Sale"
      radius={CardRadius.xs}
      height={HeightCard.sm}
      rightIconButton={
        <IoIosArrowForward onClick={() => navigate(ROUTES.REPORT_POS)} />
      }
      style={{
        backgroundColor: theme.colors.secondary[4],
        color: theme.colors.secondaryTextColor[4],
      }}
    >
      <div
        style={{
          minHeight: "25vh",
          alignItems: "center",
        }}
      >
        <Group position="apart" style={{ alignItems: "center" }}>
          <Text align="center" sx={{ fontSize: "1rem", fontWeight: 500 }}>
            Sort By:
          </Text>
          <Select
            mt={0}
            data={Object.values(SortType)}
            onChange={(e) => setSortType(e as SortType)}
            value={sortType}
            // sx={{
            //   input: {
            //     borderRadius: 20,
            //     minHeight: 35,
            //     height: 35,
            //   },
            // }}
          />
        </Group>
        <Box sx={{ maxWidth: "60%", margin: "auto", flex: 1, height: 250 }}>
          {loading ? (
            <Loader mt={80} />
          ) : data?.datasets[0]?.data.length ? (
            <Doughnut
              data={data}
              options={optionsGraph(theme)}
              width={250}
              height={250}
            />
          ) : (
            <Title order={3} pt={80} align="center">
              {MESSAGES.NO_DATA}
            </Title>
          )}
        </Box>
      </div>
    </ActionCard>
  );
}

export default memo(PointOfSale);
